<script setup>
import { computed, watch, onMounted, ref } from 'vue';
import { useMapGetter, useStore } from 'dashboard/composables/store';
import { useUISettings } from 'dashboard/composables/useUISettings';

import AccordionItem from 'dashboard/components/Accordion/AccordionItem.vue';
import ContactConversations from './ContactConversations.vue';
import ConversationAction from './ConversationAction.vue';
import ConversationParticipant from './ConversationParticipant.vue';

import GroupParticipants from './GroupParticipants.vue';

import ContactInfo from './contact/ContactInfo.vue';
import ContactDetailsItem from './ContactDetailsItem.vue';
import ConversationInfo from './ConversationInfo.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';
import Draggable from 'vuedraggable';
import MacrosList from './Macros/List.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  inboxId: {
    type: Number,
    default: undefined,
  },
  onToggle: {
    type: Function,
    default: () => {},
  },
});

const {
  updateUISettings,
  isContactSidebarItemOpen,
  conversationSidebarItemsOrder,
  toggleSidebarUIState,
} = useUISettings();

const dragging = ref(false);
const conversationSidebarItems = ref([]);

const store = useStore();
const currentChat = useMapGetter('getSelectedChat');

const conversationAttributesRef = ref({});

const conversationId = computed(() => props.conversationId);
const conversationMetadataGetter = useMapGetter(
  'conversationMetadata/getConversationMetadata'
);
const currentConversationMetaData = computed(() =>
  conversationMetadataGetter.value(conversationId.value)
);

const conversationAdditionalAttributes = computed(() => {
  return conversationAttributesRef.value;
});

watch(currentChat, (newChat) => {
  const newAttributes = newChat.additional_attributes || {};
  if (JSON.stringify(newAttributes) !== JSON.stringify(conversationAttributesRef.value)) {
     conversationAttributesRef.value = newAttributes;
  }
}, { immediate: true, deep: true });

const channelType = computed(() => currentChat.value.meta?.channel);

const contactGetter = useMapGetter('contacts/getContact');
const contactId = computed(() => currentChat.value.meta?.sender?.id);
const contact = computed(() => contactGetter.value(contactId.value));
const contactAdditionalAttributes = computed(
  () => contact.value.additional_attributes || {}
);

const getContactDetails = () => {
  if (contactId.value) {
    store.dispatch('contacts/show', { id: contactId.value });
  }
};

watch(conversationId, (newConversationId, prevConversationId) => {
  if (newConversationId && newConversationId !== prevConversationId) {
    getContactDetails();
  }
});

watch(contactId, getContactDetails);

const onPanelToggle = props.onToggle;

const onDragEnd = () => {
  dragging.value = false;
  updateUISettings({
    conversation_sidebar_items_order: conversationSidebarItems.value,
  });
};

const onShareScreen = () => {
  const windowOpen = window.open('', '', 'width=600,height=400');
  windowOpen.document.body.style.margin = '0';
  windowOpen.document.body.style.padding = '0';
  const video = windowOpen.document.createElement('img');
  video.id = 'video';
  video.style.width = '100vw';
  video.style.border = '0px';
  video.style.minHeight = '100vh';
  video.style.background = 'black';
  windowOpen.document.body.appendChild(video);
  const script = windowOpen.document.createElement('script');
  script.textContent = `const stringify = (payload = {}) => JSON.stringify(payload);
    const payload = stringify({ command: "subscribe", identifier: stringify({ channel: "RoomChannel", pubsub_token: '${this.currentChat.custom_attributes.pubsub_token}' })});
    const connection = new WebSocket('wss://chat2dev.callpicker.com/cable');
    const video = document.querySelector('#video')
    connection.onopen = function () {
    connection.send(payload);
    connection.onmessage = function (message) {
    const data = JSON.parse(message.data);
      if (data?.message?.event == 'display.share.screen') {
        window.requestAnimationFrame(() => setInterval(()=>{}, 100))
        video.src = data.message.data;
      } 
    }
  }`;
  windowOpen.document.body.appendChild(script);
  store.dispatch('shareScreen', { conversationId: this.conversationId });
};

onMounted(() => {
  conversationSidebarItems.value = conversationSidebarItemsOrder.value;
  conversationAttributesRef.value = currentChat.value.additional_attributes || {};
  getContactDetails();
  store.dispatch('attributes/get', 0);
});
</script>

<template>
  <div class="w-full">
    <ContactInfo
      :contact="contact"
      :channel-type="channelType"
      @toggle-panel="onPanelToggle"
    />
    <div class="list-group">
      <AccordionItem
        v-if="conversationAdditionalAttributes.group_conversation_participants && 
          conversationAdditionalAttributes.group_conversation_participants.length > 0"
        :title="$t('CONVERSATION_SIDEBAR.ACCORDION.GROUP_PARTICIPANTS_TITLE')"
        :is-open="isContactSidebarItemOpen('is_group_participants_open')"
        compact
        @toggle="
          value => toggleSidebarUIState('is_group_participants_open', value)
        "
      >
        <GroupParticipants
          :conversation-additional-attributes="conversationAdditionalAttributes"
        />
      </AccordionItem>
    </div>
    <div class="list-group">
      <Draggable
        :list="conversationSidebarItems"
        animation="200"
        ghost-class="ghost"
        handle=".drag-handle"
        item-key="name"
        @start="dragging = true"
        @end="onDragEnd"
      >
        <template #item="{ element }">
          <div :key="element.name" class="bg-white dark:bg-gray-800">
            <div
              v-if="element.name === 'conversation_actions'"
              class="conversation--actions"
            >
              <AccordionItem
                :title="
                  $t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_ACTIONS')
                "
                :is-open="isContactSidebarItemOpen('is_conv_actions_open')"
                @toggle="
                  value => toggleSidebarUIState('is_conv_actions_open', value)
                "
              >
                <ConversationAction
                  :conversation-id="conversationId"
                  :inbox-id="inboxId"
                />
                <!-- div class="multiselect-wrap--small">
                  <ContactDetailsItem compact :title="'Compartir Pantalla'" />
                  <div class="label-wrap">
                    <button
                      class="button-share--screen button label--add clear link primary"
                      @click="onShareScreen"
                    >
                      <span class="label secondary">Solicitar Compartir Pantalla</span>
                    </button>
                  </div>
                </div -->
              </AccordionItem>
            </div>
            <div
              v-else-if="element.name === 'conversation_participants'"
              class="conversation--actions"
            >
              <AccordionItem
                :title="$t('CONVERSATION_PARTICIPANTS.SIDEBAR_TITLE')"
                :is-open="isContactSidebarItemOpen('is_conv_participants_open')"
                @toggle="
                  value =>
                    toggleSidebarUIState('is_conv_participants_open', value)
                "
               >
                 <ConversationParticipant
                   :conversation-id="conversationId"
                   :inbox-id="inboxId"
                 />
               </AccordionItem>
            </div>
            <div v-else-if="element.name === 'conversation_info'">
              <AccordionItem
                :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONVERSATION_INFO')"
                :is-open="isContactSidebarItemOpen('is_conv_details_open')"
                compact
                @toggle="
                  value => toggleSidebarUIState('is_conv_details_open', value)
                "
              >
                <ConversationInfo
                  :conversation-attributes="conversationAdditionalAttributes"
                  :contact-attributes="contactAdditionalAttributes"
                />
              </AccordionItem>
            </div>
            <div v-else-if="element.name === 'contact_attributes'">
              <AccordionItem
                :title="$t('CONVERSATION_SIDEBAR.ACCORDION.CONTACT_ATTRIBUTES')"
                :is-open="
                  isContactSidebarItemOpen('is_contact_attributes_open')
                "
                compact
                @toggle="
                  value =>
                    toggleSidebarUIState('is_contact_attributes_open', value)
                "
              >
                <CustomAttributes
                  attribute-type="contact_attribute"
                  attribute-from="conversation_contact_panel"
                  :contact-id="contact.id"
                  :empty-state-message="
                    $t('CONVERSATION_CUSTOM_ATTRIBUTES.NO_RECORDS_FOUND')
                  "
                />
              </AccordionItem>
            </div>
            <div v-else-if="element.name === 'previous_conversation'">
              <AccordionItem
                v-if="contact.id"
                :title="
                  $t('CONVERSATION_SIDEBAR.ACCORDION.PREVIOUS_CONVERSATION')
                "
                :is-open="isContactSidebarItemOpen('is_previous_conv_open')"
                compact
                @toggle="
                  value => toggleSidebarUIState('is_previous_conv_open', value)
                "
              >
                <ContactConversations
                  :contact-id="contact.id"
                  :conversation-id="conversationId"
                />
              </AccordionItem>
            </div>
            <woot-feature-toggle
              v-else-if="element.name === 'macros'"
              feature-key="macros"
              v-show="currentChat.can_reply"
            >
              <AccordionItem
                :title="$t('CONVERSATION_SIDEBAR.ACCORDION.MACROS')"
                :is-open="isContactSidebarItemOpen('is_macro_open')"
                compact
                @toggle="value => toggleSidebarUIState('is_macro_open', value)"
              >
                <MacrosList :conversation-id="conversationId" />
              </AccordionItem>
            </woot-feature-toggle>
          </div>
        </template>
      </Draggable>
    </div>
  </div>
</template>

<style lang="scss" scoped>
::v-deep {
  .contact--profile {
    @apply pb-3 border-b border-solid border-slate-75 dark:border-slate-700;
  }
  .conversation--actions .multiselect-wrap--small {
    .multiselect {
      @apply box-border pl-6;
    }
    .multiselect__element {
      span {
        @apply w-full;
      }
    }
  }
}
</style>
